// File: Teams.js (Main component for the Teams page)

import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search as SearchIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { darken } from "polished";
import { Link } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Tab from "@mui/material/Tab";
import { SketchPicker } from "react-color";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import SearchBar from "../../components/SearchBar";

import {
  Avatar as MuiAvatar,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Checkbox,
  Chip as MuiChip,
  ClickAwayListener,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  InputBase,
  DialogActions,
  TextField,
  CardContent,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card as MuiCard,
  CardMedia as MuiCardMedia,
  CardActions,
  AvatarGroup as MuiAvatarGroup,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { green, orange, blue } from "@mui/material/colors";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy,
  Launch,
  NearMe,
  Visibility,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as MuiCloudUpload,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import { alpha } from "@mui/material/styles";
import ReusableTable from "../../../components/Common/ReusableTable";

// GraphQL queries
const GET_TEAMS = `
  query GetTeams($spaceId: ID) {
    getTeams(spaceId: $spaceId) {
      id
      name
      url
      about
      logo
      coverImage
      color
      members {
        id
        userId
        email
        role
        status
      }
      settings {
        allowMemberInvites
        visibility
        joinRequests
      }
    }
  }
`;

const CREATE_TEAM_MUTATION = `
  mutation CreateTeam($input: CreateTeamInput!) {
    createTeam(input: $input) {
      team {
        id
        name
        url
        about
        logo
        coverImage
        color
      }
    }
  }
`;

const DELETE_TEAM_MUTATION = `
  mutation DeleteTeam($id: ID!) {
    deleteTeam(id: $id)
  }
`;

const INVITE_TEAM_MEMBER_MUTATION = `
  mutation InviteTeamMember($input: InviteTeamMemberInput!) {
    inviteTeamMember(input: $input) {
      success
      message
      teamMember {
        id
        email
        role
        status
      }
    }
  }
`;

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;
const CloudUpload = styled(MuiCloudUpload)(spacing);
const CenteredContent = styled.div`
  text-align: center;
`;
const BigAvatar = styled(Avatar)`
  width: 120px;
  height: 120px;
  margin: 0 auto ${(props) => props.theme.spacing(2)};
`;

const Chip = styled(MuiChip)`
  ${spacing};
  background: ${(props) => props.paid && green[500]};
  background: ${(props) => props.sent && orange[700]};
  color: ${(props) =>
    (props.paid || props.sent) && props.theme.palette.common.white};
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
  min-width: 200px;
`;

const Customer = styled.div`
  display: flex;
  align-items: center;
`;

const CardMedia = styled(MuiCardMedia)`
  height: 220px;
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
    width: 160px;
  }
`;

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => ({
    el,
    index,
  }));
  stabilizedThis.sort((a, b) => {
    const order = comparator(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilizedThis.map((element) => element.el);
}

const headCells = [
  { id: "name", alignment: "left", label: "Team Name" },
  { id: "url", alignment: "left", label: "URL" },
  { id: "status", alignment: "left", label: "Status" },
  { id: "members", alignment: "left", label: "Members" },
  { id: "createdAt", alignment: "left", label: "Creation Date" },
];

const EnhancedTableHead = (props) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.alignment}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EnhancedTableToolbar = (props) => {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [viewStyle, setViewStyle] = React.useState("List");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const {
    numSelected,
    selectedTeams,
    onDeleteTeams,
    onInviteMembers,
    currentSpaceId,
  } = props;

  const [inviteValue, setInviteValue] = React.useState("1");
  const { t } = useTranslation();

  const handleInviteChange = (event, newValue) => {
    setInviteValue(newValue);
  };

  const { doChangeProps } = props;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInviteOpen = () => {
    setInviteDialogOpen(true);
  };

  const handleInviteClose = () => {
    setInviteDialogOpen(false);
  };

  const handleSendInvites = () => {
    // Call the parent's onInviteMembers function
    if (selectedTeams.length === 1) {
      onInviteMembers(selectedTeams[0].id);
    }
    setInviteDialogOpen(false);
  };

  const handleViewChange = (view) => {
    doChangeProps(view);
    setViewStyle(view);
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    if (selectedTeams.length === 1) {
      // Make sure we're using the correct URL format with space ID
      // http://laptop:3001/teams/spaceId/test-team-001
      const teamUrl = `${window.location.origin}/teams/${currentSpaceId}/${selectedTeams[0].url}`;

      // Feature detection for clipboard API
      if (
        navigator &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        // Modern browsers - use Clipboard API
        navigator.clipboard
          .writeText(teamUrl)
          .then(() => {
            props.onNotify("Team URL copied to clipboard", "success");
          })
          .catch((err) => {
            console.error("Could not copy text using Clipboard API: ", err);
            // If Clipboard API fails, fall back to the legacy method
            fallbackCopyTextToClipboard(teamUrl);
          });
      } else {
        // Fallback for browsers that don't support clipboard API
        fallbackCopyTextToClipboard(teamUrl);
      }
    }
  };

  // Fallback copy function using document.execCommand
  const fallbackCopyTextToClipboard = (text) => {
    try {
      // Create temporary element
      const textArea = document.createElement("textarea");
      textArea.value = text;

      // Make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);

      // Select and copy
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");

      // Remove temporary element
      document.body.removeChild(textArea);

      // Notify user
      if (successful) {
        props.onNotify("Team URL copied to clipboard", "success");
      } else {
        props.onNotify("Failed to copy URL to clipboard", "error");
      }
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
      props.onNotify(
        "Failed to copy URL to clipboard. Try manually selecting and copying the URL.",
        "error"
      );
    }
  };
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "70px",
        borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
      }}
    >
      <ToolbarTitle>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography>
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <Input placeholder={t("Search Teams")} />
            </Search>
          </Typography>
        )}
      </ToolbarTitle>
      <Spacer />
      <Box>
        {numSelected === 1 ? (
          <Box display="flex">
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                size="large"
                component={Link}
                to={`/calendar/edit-team/${selectedTeams[0].id}`}
                sx={{ mr: 1 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Preview Team">
              <IconButton
                aria-label="Preview"
                size="large"
                component={Link}
                to={`/preview/team/${selectedTeams[0].id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 1 }}
              >
                <Launch />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Link">
              <IconButton
                aria-label="Copy Link"
                size="large"
                onClick={handleCopyLink}
                sx={{ mr: 1 }}
              >
                <ContentCopy />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                size="large"
                onClick={() => onDeleteTeams(selectedTeams)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : numSelected > 1 ? (
          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete"
              size="large"
              onClick={() => onDeleteTeams(selectedTeams)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              viewStyle === "List"
                ? "Change to Card View"
                : "Change to List View"
            }
          >
            <IconButton
              color="primary"
              onClick={handleClick}
              size="medium"
              sx={{
                borderRadius: "8px",
                p: 1,
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.08),
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              {viewStyle === "List" ? <ViewModuleIcon /> : <ViewListIcon />}
            </IconButton>
          </Tooltip>
        )}
        <Menu
          id="view-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => handleViewChange("List")}>
            <ViewListIcon sx={{ mr: 1 }} /> List View
          </MenuItem>
          <MenuItem onClick={() => handleViewChange("Card")}>
            <ViewModuleIcon sx={{ mr: 1 }} /> Card View
          </MenuItem>
        </Menu>
      </Box>

      <Dialog
        open={inviteDialogOpen}
        onClose={handleInviteClose}
        aria-labelledby="invite-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="invite-dialog-title">Invite team member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add members to your team. You can invite them individually or import
            multiple at once.
          </DialogContentText>
          <Box sx={{ width: "100%", typography: "body1", mt: 2 }}>
            <TabContext value={inviteValue}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={handleInviteChange} aria-label="invite tabs">
                  <Tab label="Invite Individual" value="1" />
                  <Tab label="Bulk Import" value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      variant="outlined"
                      fullWidth
                      placeholder="Enter email address"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select label="Role" defaultValue="member">
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="owner">Owner</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="2">
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Emails"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Enter multiple emails separated by commas"
                      helperText="Format: email1@example.com, email2@example.com"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box textAlign="center" mb={2}>
                      <Typography variant="body2" color="textSecondary" mb={1}>
                        Or upload a CSV file
                      </Typography>
                      <input
                        accept=".csv"
                        style={{ display: "none" }}
                        id="csv-file-upload"
                        type="file"
                      />
                      <label htmlFor="csv-file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUpload />}
                          fullWidth
                        >
                          Upload CSV
                        </Button>
                      </label>
                      <Button color="primary" size="small" sx={{ mt: 1 }}>
                        Download Sample
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Default Role</InputLabel>
                      <Select label="Default Role" defaultValue="member">
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </TabPanel>
            </TabContext>
          </Box>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6} display="flex" alignItems="center">
              <Button
                color="primary"
                startIcon={<ContentCopy />}
                onClick={handleCopyLink}
              >
                Copy Invite Link
              </Button>
            </Grid>
            <Grid item xs={6} display="flex" justifyContent="flex-end">
              <Button onClick={handleInviteClose} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendInvites}
              >
                Send Invite
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Toolbar>
  );
};

function TeamsTable({
  teams,
  loading,
  viewStyle,
  setViewStyle,
  selected,
  setSelected,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  handleNotification,
  handleDeleteTeams,
  onInviteMembers,
  searchQuery,
  onSearchChange,
  currentSpaceId,
}) {
  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Team Name",
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => {
          // Backend now returns full URLs, so we can use the logo directly
          const logoUrl = params.row.logo;

          return (
            <Customer>
              <Avatar src={logoUrl}>
                {!params.row.logo &&
                  params.row.name &&
                  params.row.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box ml={3}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {params.row.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p">
                  {params.row.about && params.row.about.length > 50
                    ? `${params.row.about.substring(0, 50)}...`
                    : params.row.about}
                </Typography>
              </Box>
            </Customer>
          );
        },
      },
      {
        field: "url",
        headerName: "Team URL",
        flex: 1.5,
        minWidth: 300,
        renderCell: (params) => {
          // Use space ID in team URL
          const spaceId =
            new URLSearchParams(window.location.search).get("spaceId") || "";
          const fullTeamUrl = `${window.location.origin}/teams/${spaceId}/${params.row.url}`;
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                wordBreak: "break-all",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
              title={fullTeamUrl}
            >
              {fullTeamUrl}
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: () => (
          <Chip
            size="small"
            label="Active"
            sx={{ backgroundColor: green[500], color: "white" }}
          />
        ),
      },
      {
        field: "memberCount",
        headerName: "Members",
        width: 100,
        valueGetter: (params) => `${params.row.members?.length || 0} members`,
      },
      {
        field: "createdAt",
        headerName: "Creation Date",
        width: 150,
        valueGetter: (params) =>
          new Date(params.row.createdAt || Date.now()).toLocaleDateString(),
      },
    ],
    []
  );

  // Prepare data with calculated fields
  const processedTeams = React.useMemo(() => {
    return teams.map((team) => ({
      ...team,
      memberCount: team.members?.length || 0,
    }));
  }, [teams]);

  // Note: Search is handled directly by ReusableTable with onSearchChange prop

  // Event handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = teams.map((n) => n.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  // Get the selected team objects
  const selectedTeams = teams.filter((team) => selected.includes(team.id));

  // Custom toolbar actions
  const customToolbarActions = [];

  // Custom actions based on selection
  const getCustomActions = () => {
    if (selected.length === 1) {
      return [
        // Edit icon removed - it's already in the EnhancedTableToolbar
        <Tooltip key="preview" title="Preview Team">
          <IconButton
            aria-label="Preview"
            size="large"
            component={Link}
            to={`/preview/team/${selectedTeams[0].id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <Launch />
          </IconButton>
        </Tooltip>,
        <Tooltip key="copy" title="Copy Link">
          <IconButton
            aria-label="Copy Link"
            size="large"
            onClick={() => {
              const spaceId =
                new URLSearchParams(window.location.search).get("spaceId") ||
                "";
              const teamUrl = `${window.location.origin}/teams/${spaceId}/${selectedTeams[0].url}`;
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(teamUrl).then(() => {
                  handleNotification("Team URL copied to clipboard", "success");
                });
              }
            }}
            sx={{ mr: 1 }}
          >
            <ContentCopy />
          </IconButton>
        </Tooltip>,
      ];
    }
    return [];
  };

  return (
    <ReusableTable
      data={processedTeams}
      columns={columns}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search teams..."
      searchFields={["name", "url", "about"]}
      selected={selected}
      onSelectAllClick={handleSelectAllClick}
      onSelect={handleClick}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      viewStyle={viewStyle}
      setViewStyle={setViewStyle}
      hasCardView={true}
      CardComponent={TeamCard}
      cardProps={{ xs: 12, sm: 6, md: 4 }}
      onBulkEdit={
        selected.length === 1
          ? () =>
              (window.location.href = `/calendar/edit-team/${selectedTeams[0].id}`)
          : null
      }
      onBulkDelete={() => handleDeleteTeams(selectedTeams)}
      customActions={[...customToolbarActions, ...getCustomActions()]}
      title=""
      showSearch={true}
      loading={loading}
    />
  );
}

// Team Card component for Card View
const TeamCard = ({ data: team, selected: isSelected, onSelect }) => {
  return (
    <Paper>
      <Card>
        <CardMedia
          sx={{
            height: 140,
            backgroundColor: team.color || blue[500],
            position: "relative",
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={onSelect}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              background: "rgba(255,255,255,0.5)",
              borderRadius: "50%",
              padding: "4px",
              "&:hover": { background: "rgba(255,255,255,0.7)" },
            }}
          />
          {team.logo && (
            <Box
              sx={{
                position: "absolute",
                bottom: -40,
                left: 16,
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: "4px solid white",
                overflow: "hidden",
                backgroundColor: "white",
              }}
            >
              <img
                src={team.logo}
                alt={team.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}
          {!team.logo && (
            <Avatar
              sx={{
                position: "absolute",
                bottom: -40,
                left: 16,
                width: 80,
                height: 80,
                border: "4px solid white",
                fontSize: 32,
              }}
            >
              {team.name.charAt(0).toUpperCase()}
            </Avatar>
          )}
        </CardMedia>
        <CardContent sx={{ pt: 5 }}>
          <Typography gutterBottom variant="h5" component="h2">
            {team.name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            sx={{ minHeight: 60 }}
          >
            {team.about && team.about.length > 120
              ? `${team.about.substring(0, 120)}...`
              : team.about}
          </Typography>
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              size="small"
              label="Active"
              sx={{ backgroundColor: green[500], color: "white" }}
            />
            <Typography variant="body2" color="textSecondary">
              {team.members ? team.members.length : 0} members
            </Typography>
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between" }}>
          <Typography variant="caption" color="textSecondary">
            Created{" "}
            {new Date(team.createdAt || Date.now()).toLocaleDateString()}
          </Typography>
          <Button
            size="small"
            color="primary"
            startIcon={<Launch />}
            component={Link}
            to={`/preview/team/${team.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview
          </Button>
        </CardActions>
      </Card>
    </Paper>
  );
};

// Create Team Dialog Component
const CreateTeamDialog = ({ open, onClose, onSave, currentSpaceId }) => {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [about, setAbout] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [color, setColor] = useState("#2196F3");
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [isUrlManuallyEdited, setIsUrlManuallyEdited] = useState(false);
  const colorPickerAnchorRef = React.useRef(null);

  // Function to convert string to slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w\-]+/g, "") // Remove all non-word chars
      .replace(/\-\-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  };

  // Direct effect that forces URL update whenever name changes
  // This will run regardless of any other state, ensuring URL always updates with name
  useEffect(() => {
    if (name && !isUrlManuallyEdited) {
      const slug = convertToSlug(name);
      setUrl(slug);
      setUrlError(""); // Clear any URL error when auto-generating
    }
  }, [name, isUrlManuallyEdited]);

  // Handle team name changes
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    handleUrlChange(e);
    // URL update is handled by the useEffect above
  };

  // Handle URL input with validation and formatting
  const handleUrlChange = (event) => {
    // Mark that user has manually edited the URL field
    setIsUrlManuallyEdited(true);

    const value = event.target.value;

    // Format the URL (lowercase, replace spaces with hyphens, remove special chars)
    const formattedUrl = value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    // Show message if input contains invalid characters, but still update the field
    if (value !== formattedUrl) {
      setUrlError(
        "URL can only contain lowercase letters, numbers, and hyphens"
      );
    } else {
      setUrlError("");
    }

    // Always update with the formatted value
    setUrl(formattedUrl);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    const teamData = {
      name,
      url,
      about,
      color,
      logo: logo, // Include the logo file
    };
    onSave(teamData);
    resetForm();
  };

  const resetForm = () => {
    setName("");
    setUrl("");
    setAbout("");
    setLogo(null);
    setLogoPreview("");
    setColor("#2196F3");
    setColorPickerOpen(false);
    setUrlError("");
    setIsUrlManuallyEdited(false);
  };

  // Reset all flags when dialog opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Team</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <CenteredContent>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="team-logo-upload"
                type="file"
                onChange={handleLogoChange}
              />
              {logoPreview ? (
                <BigAvatar src={logoPreview} />
              ) : (
                <BigAvatar sx={{ bgcolor: color }}>
                  {name ? name.charAt(0).toUpperCase() : "T"}
                </BigAvatar>
              )}
              <label htmlFor="team-logo-upload">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                >
                  Upload Team Logo
                </Button>
              </label>
              <Typography
                variant="caption"
                display="block"
                gutterBottom
                sx={{ mt: 1 }}
              >
                For best results, use a square image at least 128px by 128px in
                .jpg format
              </Typography>
            </CenteredContent>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Team Name"
              value={name}
              onChange={handleNameChange}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Team URL"
              value={url}
              onChange={handleUrlChange}
              variant="outlined"
              fullWidth
              required
              error={!!urlError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography color="textSecondary">
                      {window.location.origin}/teams/
                      {currentSpaceId || "{spaceId}"}/
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText={
                urlError ||
                "Only lowercase letters, numbers, and hyphens are allowed"
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="About"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              helperText="Briefly describe your team (optional)"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Team Color
            </Typography>
            <Box display="flex" alignItems="center">
              <Box
                ref={colorPickerAnchorRef}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  mr: 2,
                  bgcolor: color,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  cursor: "pointer",
                }}
                onClick={() => setColorPickerOpen(!colorPickerOpen)}
              />
              <TextField
                value={color}
                onChange={(e) => setColor(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ width: 120 }}
              />
            </Box>
            <Popper
              open={colorPickerOpen}
              anchorEl={colorPickerAnchorRef.current}
              placement="bottom-start"
              style={{ zIndex: 1301 }}
              modifiers={[
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                    padding: 8,
                  },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["top-start", "right", "left"],
                  },
                },
              ]}
            >
              <ClickAwayListener onClickAway={() => setColorPickerOpen(false)}>
                <Box
                  sx={{
                    mt: 1,
                    boxShadow: 3,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <SketchPicker
                    color={color}
                    onChangeComplete={(newColor) => setColor(newColor.hex)}
                  />
                </Box>
              </ClickAwayListener>
            </Popper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!name || !url}
        >
          Create Team
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function Teams() {
  // State variables
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewStyle, setViewStyle] = useState("List");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    teams: [],
  });

  // Context and hooks
  const { currentSpace } = useSpace();
  const location = window.location;

  // Get spaceId from query params or use current space
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Fetch teams data
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post("/graphql", {
          query: GET_TEAMS,
          variables: { spaceId: currentSpaceId },
        });

        if (response.data?.errors) {
          console.error("GraphQL errors:", response.data.errors);
          handleNotification("Failed to load teams", "error");
        } else {
          // Check both possible response structures
          const teamsData =
            response.data?.data?.getTeams || response.data?.getTeams || [];
          console.log("Teams data fetched:", teamsData);
          setTeams(teamsData);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
        handleNotification("Failed to load teams", "error");
      } finally {
        setLoading(false);
      }
    };

    if (currentSpaceId) {
      fetchTeams();
    }
  }, [currentSpaceId]);

  // Page and row handling
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search handling
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when search changes
  };

  // Notification handling
  const handleNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Helper function to show user-friendly error messages
  const showErrorMessage = (errorMessage) => {
    console.log("=== Processing error message ===");
    console.log("Raw error message:", errorMessage);

    // Check for duplicate key errors
    if (errorMessage.includes("E11000 duplicate key error")) {
      console.log("Detected MongoDB duplicate key error");

      if (errorMessage.includes("url_1_spaceId_1")) {
        console.log("URL duplicate detected");
        handleNotification(
          "Team URL already exists in this space. Please choose a different URL.",
          "error"
        );
        return;
      }

      if (errorMessage.includes("name_1_spaceId_1")) {
        console.log("Name duplicate detected");
        handleNotification(
          "Team name already exists in this space. Please choose a different name.",
          "error"
        );
        return;
      }

      // Generic duplicate error
      console.log("Generic duplicate detected");
      handleNotification(
        "A team with this name or URL already exists. Please choose different values.",
        "error"
      );
      return;
    }

    // Check for custom validation messages from our resolver
    if (
      errorMessage.includes("Team name") &&
      errorMessage.includes("already exists")
    ) {
      console.log("Custom name validation message");
      handleNotification(errorMessage, "error");
      return;
    }

    if (
      errorMessage.includes("Team URL") &&
      errorMessage.includes("already exists")
    ) {
      console.log("Custom URL validation message");
      handleNotification(errorMessage, "error");
      return;
    }

    // Fallback generic error
    console.log("Generic error message");
    handleNotification(errorMessage || "Failed to create team", "error");
  };

  // Team creation with file upload support
  const handleCreateTeam = async (teamData) => {
    try {
      setLoading(true);
      console.log("=== Creating team ===");
      console.log("Team data:", teamData);

      // Separate the logo from other team data
      const { logo, ...teamInput } = teamData;
      const variables = {
        input: {
          ...teamInput,
          spaceId: currentSpaceId,
        },
      };

      let response;

      // Handle logo upload if present
      if (logo) {
        console.log("Creating team with logo");
        const formData = new FormData();
        variables.input.logo = logo;

        const operations = {
          query: CREATE_TEAM_MUTATION,
          variables: variables,
        };

        formData.append("operations", JSON.stringify(operations));
        formData.append("map", JSON.stringify({ 0: ["variables.input.logo"] }));
        formData.append("0", logo);

        response = await axiosInstance.post("/graphql", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        console.log("Creating team without logo");
        response = await axiosInstance.post("/graphql", {
          query: CREATE_TEAM_MUTATION,
          variables: variables,
        });
      }

      console.log("=== GraphQL Response ===");
      console.log("Full response:", JSON.stringify(response, null, 2));
      console.log("Response data:", JSON.stringify(response.data, null, 2));

      // Check for errors in multiple possible locations
      let hasErrors = false;
      let errorMessage = null;

      // Check for errors directly on response
      if (response.errors && response.errors.length > 0) {
        console.log("Found errors at response.errors");
        hasErrors = true;
        errorMessage = response.errors[0].message;
      }
      // Check for errors in response.data.errors
      else if (
        response.data &&
        response.data.errors &&
        response.data.errors.length > 0
      ) {
        console.log("Found errors at response.data.errors");
        hasErrors = true;
        errorMessage = response.data.errors[0].message;
      }

      if (hasErrors) {
        console.log("Processing error:", errorMessage);
        showErrorMessage(errorMessage);
        return;
      }

      // Handle successful response - try multiple possible response structures
      let newTeam = null;

      // Try different possible response structures
      if (response.data?.data?.createTeam?.team) {
        console.log("Found team at response.data.data.createTeam.team");
        newTeam = response.data.data.createTeam.team;
      } else if (response.data?.createTeam?.team) {
        console.log("Found team at response.data.createTeam.team");
        newTeam = response.data.createTeam.team;
      } else if (response.data?.team) {
        console.log("Found team at response.data.team");
        newTeam = response.data.team;
      } else if (response.createTeam?.team) {
        console.log("Found team at response.createTeam.team");
        newTeam = response.createTeam.team;
      }

      if (newTeam) {
        console.log("Team created successfully:", newTeam);
        setTeams([...teams, newTeam]);
        handleNotification("Team created successfully", "success");
      } else {
        console.log("No team data found in response - debugging structure:");
        console.log("Full response:", JSON.stringify(response, null, 2));
        console.log("response.data:", response.data);
        console.log("response.data.data:", response.data?.data);
        console.log("response.data.createTeam:", response.data?.createTeam);

        // If data is null but no errors, might be a different error structure
        if (response.data === null || response.data?.data === null) {
          console.log("Data is null - this might indicate an error occurred");
          showErrorMessage(
            "Failed to create team - please check if team name or URL already exists"
          );
        } else {
          showErrorMessage("Failed to create team - no data returned");
        }
      }
    } catch (error) {
      console.log("=== Exception caught ===");
      console.log("Error object:", error);
      console.log("Error response:", JSON.stringify(error.response, null, 2));

      let errorMessage = null;
      let hasError = false;

      // Check for errors in error.response.data.errors (most common)
      if (
        error.response?.data?.errors &&
        error.response.data.errors.length > 0
      ) {
        console.log("Error found in error.response.data.errors");
        errorMessage = error.response.data.errors[0].message;
        hasError = true;
      }
      // Check for errors in error.response.errors
      else if (error.response?.errors && error.response.errors.length > 0) {
        console.log("Error found in error.response.errors");
        errorMessage = error.response.errors[0].message;
        hasError = true;
      }
      // Check for direct error message
      else if (error.message) {
        console.log("Error found in error.message");
        errorMessage = error.message;
        hasError = true;
      }

      if (hasError && errorMessage) {
        console.log("Processing caught error:", errorMessage);
        showErrorMessage(errorMessage);
      } else {
        console.log("Unknown error structure");
        showErrorMessage("Failed to create team - unknown error occurred");
      }
    } finally {
      setLoading(false);
      setCreateDialogOpen(false);
    }
  };

  // Team deletion
  const handleDeleteTeams = (teamsToDelete) => {
    setConfirmDelete({
      open: true,
      teams: teamsToDelete,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const deletePromises = confirmDelete.teams.map((team) =>
        axiosInstance.post("/graphql", {
          query: DELETE_TEAM_MUTATION,
          variables: { id: team.id },
        })
      );

      await Promise.all(deletePromises);

      // Remove deleted teams from state
      const teamIdsToDelete = confirmDelete.teams.map((team) => team.id);
      setTeams(teams.filter((team) => !teamIdsToDelete.includes(team.id)));
      setSelected([]);

      handleNotification(
        `Successfully deleted ${confirmDelete.teams.length} team(s)`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting teams:", error);
      handleNotification("Failed to delete teams", "error");
    } finally {
      setLoading(false);
      setConfirmDelete({ open: false, teams: [] });
    }
  };

  // Member invitation
  const handleInviteMembers = async (teamId) => {
    // This would be implemented with your invite mutation in a real app
    handleNotification(
      "Invitation feature is ready in the UI, but not yet connected to backend",
      "info"
    );
  };

  return (
    <React.Fragment>
      <Helmet title="Teams" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Teams
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${currentSpaceId}`}
            >
              Calendar
            </Link>
            <Typography>Teams</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            New Team
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <TeamsTable
            teams={teams}
            loading={loading}
            viewStyle={viewStyle}
            setViewStyle={setViewStyle} // Make sure to pass this prop here
            selected={selected}
            setSelected={setSelected}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleNotification={handleNotification}
            handleDeleteTeams={handleDeleteTeams}
            onInviteMembers={handleInviteMembers}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            currentSpaceId={currentSpaceId}
          />
        </Grid>
      </Grid>

      {/* Create Team Dialog */}
      <CreateTeamDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateTeam}
        currentSpaceId={currentSpaceId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, teams: [] })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete.teams.length === 1
              ? `Are you sure you want to delete the team "${confirmDelete.teams[0]?.name}"?`
              : `Are you sure you want to delete ${confirmDelete.teams.length} selected teams?`}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, teams: [] })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default Teams;
